import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Branding as BrandingIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  LocalShipping as ShippingIcon,
  PriceCheck as PriceIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Warehouse as WarehouseIcon,
  QrCode as QrCodeIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useCompany } from '../context/CompanyContext';
import stockService from '../services/stockService';
import './ProductManagement.css';

const ProductManagement = () => {
  const { companyId } = useCompany();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStock, setFilterStock] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    // Temel Bilgiler
    name: '',
    subName: '',
    productCode: '',
    brand: '',
    category: '',
    subCategory: '',
    title: '',
    seoUrl: '',
    htmlDescription: '',
    
    // Boyutlar
    dimensions: {
      length: 0,
      width: 0,
      height: 0,
      weight: 0
    },
    
    // Birim ve Fiyatlandırma
    unit: '',
    purchasePrice: 0,
    salePrice: 0,
    taxRate: 18,
    
    // Stok Bilgileri
    stock: 0,
    minStock: 0,
    maxStock: 0,
    reservedStock: 0,
    
    // Kodlar
    barcode: '',
    supplierCode: '',
    oemNumbers: [],
    crossReferenceCodes: [],
    
    // Uyumluluk
    compatibleVehicles: [],
    compatibleDevices: [],
    
    // Durum
    isActive: true,
    isCombined: false,
    combinedProducts: [],
    
    // Konum
    warehouseLocation: {
      warehouseId: '',
      x: 0,
      y: 0,
      z: 0,
      aisle: '',
      rack: '',
      level: '',
      position: '',
      locationType: 'storage',
      barcode: '',
      qrCode: ''
    },
    
    // B2B Ayarları
    b2bSettings: {
      isVisible: true,
      specialPricing: false,
      minimumOrderQuantity: 1,
      maximumOrderQuantity: 0,
      leadTime: 1,
      stockReservation: false,
      customerTypePrices: []
    },
    
    // B2C Ayarları
    b2cSettings: {
      isVisible: true,
      specialPricing: false,
      campaignEligible: false,
      couponEligible: true,
      loyaltyPointsEligible: true,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: '',
      displayOrder: 0,
      stockAlertThreshold: 10,
      shippingRules: [],
      reviewSettings: {
        allowReviews: true,
        requireApproval: false,
        autoApprove: false
      }
    },
    
    // Notlar
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // API çağrıları
      const [categoriesData, brandsData, unitsData, suppliersData, warehousesData] = await Promise.all([
        stockService.getCategories(),
        stockService.getBrands(),
        stockService.getUnits(),
        stockService.getSuppliers(),
        stockService.getWarehouses()
      ]);
      
      setCategories(categoriesData);
      setBrands(brandsData);
      setUnits(unitsData);
      setSuppliers(suppliersData);
      setWarehouses(warehousesData);
      
      await loadProducts();
    } catch (error) {
      showSnackbar('Veri yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const productsData = await stockService.getProducts();
      setProducts(productsData);
    } catch (error) {
      showSnackbar('Ürünler yüklenirken hata oluştu', 'error');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editingProduct) {
        await stockService.updateProduct(editingProduct.id, formData);
        showSnackbar('Ürün başarıyla güncellendi', 'success');
      } else {
        await stockService.createProduct(formData);
        showSnackbar('Ürün başarıyla eklendi', 'success');
      }
      setOpenDialog(false);
      resetForm();
      await loadProducts();
    } catch (error) {
      showSnackbar('Ürün kaydedilirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      setLoading(true);
      try {
        await stockService.deleteProduct(productId);
        showSnackbar('Ürün başarıyla silindi', 'success');
        await loadProducts();
      } catch (error) {
        showSnackbar('Ürün silinirken hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      ...formData,
      name: product.name,
      subName: product.subName,
      productCode: product.productCode,
      brand: product.brand,
      category: product.category,
      stock: product.stock,
      salePrice: product.salePrice,
      isActive: product.isActive
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subName: '',
      productCode: '',
      brand: '',
      category: '',
      subCategory: '',
      title: '',
      seoUrl: '',
      htmlDescription: '',
      dimensions: { length: 0, width: 0, height: 0, weight: 0 },
      unit: '',
      purchasePrice: 0,
      salePrice: 0,
      taxRate: 18,
      stock: 0,
      minStock: 0,
      maxStock: 0,
      reservedStock: 0,
      barcode: '',
      supplierCode: '',
      oemNumbers: [],
      crossReferenceCodes: [],
      compatibleVehicles: [],
      compatibleDevices: [],
      isActive: true,
      isCombined: false,
      combinedProducts: [],
      warehouseLocation: {
        warehouseId: '',
        x: 0,
        y: 0,
        z: 0,
        aisle: '',
        rack: '',
        level: '',
        position: '',
        locationType: 'storage',
        barcode: '',
        qrCode: ''
      },
      b2bSettings: {
        isVisible: true,
        specialPricing: false,
        minimumOrderQuantity: 1,
        maximumOrderQuantity: 0,
        leadTime: 1,
        stockReservation: false,
        customerTypePrices: []
      },
      b2cSettings: {
        isVisible: true,
        specialPricing: false,
        campaignEligible: false,
        couponEligible: true,
        loyaltyPointsEligible: true,
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        displayOrder: 0,
        stockAlertThreshold: 10,
        shippingRules: [],
        reviewSettings: {
          allowReviews: true,
          requireApproval: false,
          autoApprove: false
        }
      },
      notes: ''
    });
    setEditingProduct(null);
    setActiveTab(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.productCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !filterCategory || product.category === filterCategory;
    const matchesBrand = !filterBrand || product.brand === filterBrand;
    
    let matchesStock = true;
    if (filterStock === 'low') {
      matchesStock = product.stock <= 10;
    } else if (filterStock === 'out') {
      matchesStock = product.stock === 0;
    }
    
    return matchesSearch && matchesCategory && matchesBrand && matchesStock;
  });

  const renderBasicInfoTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Ürün Adı"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Alt Ad"
          value={formData.subName}
          onChange={(e) => setFormData({...formData, subName: e.target.value})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Ürün Kodu"
          value={formData.productCode}
          onChange={(e) => setFormData({...formData, productCode: e.target.value})}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Marka</InputLabel>
          <Select
            value={formData.brand}
            onChange={(e) => setFormData({...formData, brand: e.target.value})}
          >
            {brands.map(brand => (
              <MenuItem key={brand.id} value={brand.name}>{brand.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Kategori</InputLabel>
          <Select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
          >
            {categories.map(category => (
              <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Alt Kategori"
          value={formData.subCategory}
          onChange={(e) => setFormData({...formData, subCategory: e.target.value})}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Açıklama"
          value={formData.htmlDescription}
          onChange={(e) => setFormData({...formData, htmlDescription: e.target.value})}
        />
      </Grid>
    </Grid>
  );

  const renderPricingTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Alış Fiyatı"
          value={formData.purchasePrice}
          onChange={(e) => setFormData({...formData, purchasePrice: parseFloat(e.target.value) || 0})}
          InputProps={{
            startAdornment: <InputAdornment position="start">₺</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Satış Fiyatı"
          value={formData.salePrice}
          onChange={(e) => setFormData({...formData, salePrice: parseFloat(e.target.value) || 0})}
          InputProps={{
            startAdornment: <InputAdornment position="start">₺</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="KDV Oranı (%)"
          value={formData.taxRate}
          onChange={(e) => setFormData({...formData, taxRate: parseFloat(e.target.value) || 0})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Birim</InputLabel>
          <Select
            value={formData.unit}
            onChange={(e) => setFormData({...formData, unit: e.target.value})}
          >
            {units.map(unit => (
              <MenuItem key={unit.id} value={unit.name}>{unit.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderStockTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Mevcut Stok"
          value={formData.stock}
          onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value) || 0})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Minimum Stok"
          value={formData.minStock}
          onChange={(e) => setFormData({...formData, minStock: parseInt(e.target.value) || 0})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Maksimum Stok"
          value={formData.maxStock}
          onChange={(e) => setFormData({...formData, maxStock: parseInt(e.target.value) || 0})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Rezerve Stok"
          value={formData.reservedStock}
          onChange={(e) => setFormData({...formData, reservedStock: parseInt(e.target.value) || 0})}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
            />
          }
          label="Aktif"
        />
      </Grid>
    </Grid>
  );

  const renderB2BSettingsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.b2bSettings.isVisible}
              onChange={(e) => setFormData({
                ...formData, 
                b2bSettings: {...formData.b2bSettings, isVisible: e.target.checked}
              })}
            />
          }
          label="B2B'de Görünür"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Minimum Sipariş Miktarı"
          value={formData.b2bSettings.minimumOrderQuantity}
          onChange={(e) => setFormData({
            ...formData, 
            b2bSettings: {...formData.b2bSettings, minimumOrderQuantity: parseInt(e.target.value) || 1}
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Maksimum Sipariş Miktarı"
          value={formData.b2bSettings.maximumOrderQuantity}
          onChange={(e) => setFormData({
            ...formData, 
            b2bSettings: {...formData.b2bSettings, maximumOrderQuantity: parseInt(e.target.value) || 0}
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Tedarik Süresi (Gün)"
          value={formData.b2bSettings.leadTime}
          onChange={(e) => setFormData({
            ...formData, 
            b2bSettings: {...formData.b2bSettings, leadTime: parseInt(e.target.value) || 1}
          })}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.b2bSettings.stockReservation}
              onChange={(e) => setFormData({
                ...formData, 
                b2bSettings: {...formData.b2bSettings, stockReservation: e.target.checked}
              })}
            />
          }
          label="Stok Rezervasyonu"
        />
      </Grid>
    </Grid>
  );

  const renderB2CSettingsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.b2cSettings.isVisible}
              onChange={(e) => setFormData({
                ...formData, 
                b2cSettings: {...formData.b2cSettings, isVisible: e.target.checked}
              })}
            />
          }
          label="B2C'de Görünür"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="SEO Başlığı"
          value={formData.b2cSettings.seoTitle}
          onChange={(e) => setFormData({
            ...formData, 
            b2cSettings: {...formData.b2cSettings, seoTitle: e.target.value}
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Stok Uyarı Eşiği"
          value={formData.b2cSettings.stockAlertThreshold}
          onChange={(e) => setFormData({
            ...formData, 
            b2cSettings: {...formData.b2cSettings, stockAlertThreshold: parseInt(e.target.value) || 10}
          })}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.b2cSettings.couponEligible}
              onChange={(e) => setFormData({
                ...formData, 
                b2cSettings: {...formData.b2cSettings, couponEligible: e.target.checked}
              })}
            />
          }
          label="Kupon Uygunluğu"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.b2cSettings.reviewSettings.allowReviews}
              onChange={(e) => setFormData({
                ...formData, 
                b2cSettings: {
                  ...formData.b2cSettings, 
                  reviewSettings: {...formData.b2cSettings.reviewSettings, allowReviews: e.target.checked}
                }
              })}
            />
          }
          label="Yorumlara İzin Ver"
        />
      </Grid>
    </Grid>
  );

  const renderWarehouseTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Depo</InputLabel>
          <Select
            value={formData.warehouseLocation.warehouseId}
            onChange={(e) => setFormData({
              ...formData, 
              warehouseLocation: {...formData.warehouseLocation, warehouseId: e.target.value}
            })}
          >
            {warehouses.map(warehouse => (
              <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Koridor"
          value={formData.warehouseLocation.aisle}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, aisle: e.target.value}
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="X Koordinatı"
          value={formData.warehouseLocation.x}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, x: parseFloat(e.target.value) || 0}
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Y Koordinatı"
          value={formData.warehouseLocation.y}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, y: parseFloat(e.target.value) || 0}
          })}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Z Koordinatı"
          value={formData.warehouseLocation.z}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, z: parseFloat(e.target.value) || 0}
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Barkod"
          value={formData.warehouseLocation.barcode}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, barcode: e.target.value}
          })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="QR Kod"
          value={formData.warehouseLocation.qrCode}
          onChange={(e) => setFormData({
            ...formData, 
            warehouseLocation: {...formData.warehouseLocation, qrCode: e.target.value}
          })}
        />
      </Grid>
    </Grid>
  );

  const tabPanels = [
    { label: 'Temel Bilgiler', icon: <DescriptionIcon />, content: renderBasicInfoTab() },
    { label: 'Fiyatlandırma', icon: <PriceIcon />, content: renderPricingTab() },
    { label: 'Stok', icon: <InventoryIcon />, content: renderStockTab() },
    { label: 'B2B Ayarları', icon: <BusinessIcon />, content: renderB2BSettingsTab() },
    { label: 'B2C Ayarları', icon: <PersonIcon />, content: renderB2CSettingsTab() },
    { label: 'Depo Konumu', icon: <WarehouseIcon />, content: renderWarehouseTab() }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Ürün Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Yeni Ürün Ekle
          </Button>
        </Box>

        {/* Arama ve Filtreler */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Marka</InputLabel>
              <Select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.name}>{brand.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Stok Durumu</InputLabel>
              <Select
                value={filterStock}
                onChange={(e) => setFilterStock(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="low">Düşük Stok</MenuItem>
                <MenuItem value="out">Stok Yok</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Ürün Tablosu */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ürün Adı</TableCell>
                <TableCell>Kod</TableCell>
                <TableCell>Marka</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Stok</TableCell>
                <TableCell>Fiyat</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{product.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {product.subName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{product.productCode}</TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.stock}
                      color={product.stock === 0 ? 'error' : product.stock <= 10 ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>₺{product.salePrice}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.isActive ? 'Aktif' : 'Pasif'}
                      color={product.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(product)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(product.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Ürün Ekleme/Düzenleme Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabPanels.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            {tabPanels[activeTab].content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductManagement;
